/**
 * targeting-draft — the workshop runner's LOCAL, per-user draft of the Targeting editor.
 *
 * Curating the five keyword/role/location lists is a multi-minute editing session with a single
 * explicit »speichern« at the end, and the textareas are uncontrolled — the typed lines exist
 * nowhere but the DOM until that click. A reload, or the data-plane 401 route-to-login
 * round-trip (`services.ts`), re-seeds the form from the last SAVED config, so the whole
 * re-curation is gone. And because the save is a whole-config replace, the person cannot even
 * flush half of it early.
 *
 * Two deliberate shape decisions:
 *
 *   1. It stores the RAW textarea text per field, not parsed lists. Mid-edit text has blank
 *      lines and half-typed entries; splitting on save (as the surface already does) keeps the
 *      rescue faithful to what the person actually typed.
 *   2. It holds ONLY the five keys the form renders. The save spreads the current server config
 *      and overwrites just those keys, because the fold replaces `targeting` wholesale —
 *      `sourceAllowlist`, `allowCompanyWebsites`, `minTier` and `limit` are destroyed by a save
 *      that rebuilds the config from the form. A draft that could carry other keys would put
 *      that invariant one careless spread away from breaking, so it structurally cannot.
 *
 * `seededFrom` records the server values the form started from. If a save landed from somewhere
 * else in the meantime, the draft is describing a config that no longer exists and is dropped
 * rather than replayed over the newer truth.
 */

import { createLocalDraftStore, type LocalDraftStore } from "./local-draft"

/** The persisted editor state, keyed by the rendered field names. */
export interface TargetingDraft {
  /** Raw textarea text per rendered field — exactly what the person typed. */
  fields: Record<string, string>
  /** The server text the form was seeded from, per field — the staleness fingerprint. */
  seededFrom: Record<string, string>
  /** When this draft was last touched (ISO-8601). */
  updatedAt: string
}

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null

const isStringMap = (v: unknown): v is Record<string, string> =>
  isRecord(v) && Object.values(v).every((x) => typeof x === "string")

/** A total, dependency-free guard for a persisted targeting draft. */
export function isTargetingDraft(v: unknown): v is TargetingDraft {
  return isRecord(v) && isStringMap(v["fields"]) && isStringMap(v["seededFrom"]) && typeof v["updatedAt"] === "string"
}

export type TargetingDraftStore = LocalDraftStore<TargetingDraft>

const KEY_PREFIX = "career:workshop-targeting:draft:v1:"

/** A per-user targeting-draft store, guarded by {@link isTargetingDraft}. */
export function createTargetingDraftStore(userId: string | null, explicitStorage?: Storage): TargetingDraftStore {
  return createLocalDraftStore(KEY_PREFIX, userId, isTargetingDraft, explicitStorage)
}

/**
 * The rescued text per field, or `{}` when there is nothing to restore.
 *
 * A draft whose `seededFrom` no longer matches the server is discarded whole: it describes an
 * edit of a config that has since changed, and replaying half of it over newer truth is worse
 * than starting from what the server now says.
 */
export function rescuedFields(draft: TargetingDraft | null, serverText: Record<string, string>): Record<string, string> {
  if (!draft) return {}
  const keys = new Set([...Object.keys(draft.seededFrom), ...Object.keys(serverText)])
  for (const key of keys) {
    if ((draft.seededFrom[key] ?? "") !== (serverText[key] ?? "")) return {}
  }
  return { ...draft.fields }
}

/**
 * True when the edited text is identical to what the server already holds — nothing to rescue.
 *
 * Only the keys the person has actually touched are compared. A field ABSENT from `fields`
 * means "still showing the server's value", not "emptied": treating absence as an empty string
 * would report every partially-edited form as changed and leave a draft behind forever.
 */
export function isUnchanged(fields: Record<string, string>, serverText: Record<string, string>): boolean {
  return Object.entries(fields).every(([key, text]) => text === (serverText[key] ?? ""))
}
